// src/pages/PrivacyPolicyPage.js
import React from 'react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';

const PrivacyPolicyPage = () => {
  return (
    <div className="flex flex-col min-h-screen bg-background-primary">
      <MainNavbar />

      <main className="flex-grow">
        <div className="max-w-4xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          {/* Main Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-extrabold text-content-primary sm:text-5xl tracking-tight">Privacy Policy</h1>
            <p className="mt-4 text-lg text-content-secondary">Last updated: March 6, 2026</p>
          </div>

          {/* Article content with prose styling */}
          <article className="prose prose-lg lg:prose-xl max-w-none dark:prose-invert">

            <h2>1. Introduction</h2>
            <p>
              Welcome to Kikikata. This Privacy Policy explains what personal data we collect, how we use it, who we share it with, and what rights
              you have over your data. By using our website and services, you agree to the practices described in this policy.
            </p>
            <p>
              Kikikata&apos;s content (words, sentences, audio) is publicly accessible to everyone. This policy relates primarily to data collected
              when you create an account and use our Spaced Repetition System (SRS) features.
            </p>

            <h2>2. Information We Collect</h2>

            <h3>2.1 Information You Provide</h3>
            <ul>
              <li>
                <strong>Email address</strong>: Required when registering with email and password, or retrieved from your Google account when
                signing in with Google.
              </li>
              <li>
                <strong>Password</strong>: When using email/password sign-in. Your password is never stored by us &mdash; it is hashed and managed
                entirely by Firebase Authentication.
              </li>
            </ul>

            <h3>2.2 Data Generated by Your Use of the Service</h3>
            <ul>
              <li>
                <strong>SRS learning data</strong>: Your progress through words and sentences, review history, and scheduled review dates,
                used to power the spaced repetition system.
              </li>
              <li>
                <strong>Performance data</strong>: Answer accuracy per session, used to generate your progress statistics and dashboard.
              </li>
              <li>
                <strong>Streak and milestone data</strong>: Your daily review streaks and learning achievements.
              </li>
              <li>
                <strong>Account settings</strong>: Your study preferences, notification settings, and timezone.
              </li>
              <li>
                <strong>Subscription data</strong>: Your active plan, billing status, and a subscription reference ID from PayPal.
                We do not store payment card details &mdash; these are handled entirely by PayPal.
              </li>
              <li>
                <strong>Account metadata</strong>: Account creation date and email verification status.
              </li>
            </ul>

            <h3>2.3 Data Collected Automatically</h3>
            <ul>
              <li>
                <strong>Usage and analytics data</strong>: Firebase may collect device information, IP addresses, and interaction data as part
                of its standard analytics and security services. See{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google&apos;s Privacy Policy</a> for details.
              </li>
              <li>
                <strong>Support chat data</strong>: If you interact with our live chat (Crisp), your messages and your email address (if you are
                logged in) are shared with Crisp. See{' '}
                <a href="https://crisp.chat/en/privacy/" target="_blank" rel="noopener noreferrer">Crisp&apos;s Privacy Policy</a> for details.
              </li>
            </ul>

            <h3>2.4 Data Stored Locally on Your Device</h3>
            <p>
              We store the following preferences in your browser&apos;s local storage. This data never leaves your device and is not sent to our servers:
            </p>
            <ul>
              <li>Session settings (e.g., sound effects, animations, input mode)</li>
              <li>Theme preference (light or dark mode)</li>
              <li>Audio playback speed</li>
              <li>Furigana visibility preference</li>
              <li>Translation visibility preference</li>
            </ul>
            <p>
              Firebase Authentication also uses browser local storage and IndexedDB to persist your login session across page reloads.
            </p>

            <h2>3. How We Use Your Information</h2>
            <ul>
              <li>To create and manage your account.</li>
              <li>To provide the SRS learning and review experience personalized to your progress.</li>
              <li>To verify your email address and authenticate you securely.</li>
              <li>To process your subscription payments via PayPal.</li>
              <li>To send you transactional emails (email verification, weekly progress reports if enabled in your settings) via Amazon Web Services (SES).</li>
              <li>To display your learning statistics and progress on your dashboard.</li>
              <li>To improve the performance, security, and user experience of our service.</li>
              <li>To respond to support requests submitted via Crisp chat.</li>
            </ul>

            <h2>4. Third-Party Services We Use</h2>
            <p>We share your data with the following trusted providers to deliver our services:</p>
            <ul>
              <li>
                <strong>Google Firebase</strong>: Provides authentication, database storage (Firestore), and cloud backend functions. Your
                account data and learning progress are stored in Firebase.{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>
              </li>
              <li>
                <strong>PayPal</strong>: Processes subscription and one-time payments. When you subscribe, your payment details are entered
                directly on PayPal&apos;s platform. We receive only a subscription ID and your Kikikata user ID from PayPal.{' '}
                <a href="https://www.paypal.com/us/legalhub/privacy-full" target="_blank" rel="noopener noreferrer">PayPal Privacy Policy</a>
              </li>
              <li>
                <strong>Crisp</strong>: Provides the live support chat widget displayed to logged-in users. When you use the chat, your email
                address and conversation are processed by Crisp.{' '}
                <a href="https://crisp.chat/en/privacy/" target="_blank" rel="noopener noreferrer">Crisp Privacy Policy</a>
              </li>
              <li>
                <strong>Amazon Web Services (SES)</strong>: Handles delivery of optional weekly progress report emails.{' '}
                <a href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer">AWS Privacy Policy</a>
              </li>
            </ul>

            <h2>5. International Data Transfers</h2>
            <p>
              Our service uses infrastructure hosted primarily in the United States (Firebase, Amazon Web Services). If you are located in the
              European Economic Area (EEA), United Kingdom, or another region with data transfer restrictions, your data may be transferred to
              and processed in the United States. Firebase and AWS participate in frameworks designed to ensure adequate data protection for
              such transfers.
            </p>

            <h2>6. Data Security</h2>
            <p>
              Your data is stored and processed using Google Firebase, which provides enterprise-grade security infrastructure including encryption
              in transit and at rest. Passwords are never held by us &mdash; they are managed entirely by Firebase Authentication. Payment processing
              is handled exclusively by PayPal on their secure platform; we never receive or store your payment card details.
            </p>
            <p>
              The security of your data depends on the infrastructure provided by these third-party services. We are not responsible for security
              incidents arising within their platforms. We encourage you to review the security practices of{' '}
              <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">Google Firebase</a> and{' '}
              <a href="https://www.paypal.com/us/legalhub/privacy-full" target="_blank" rel="noopener noreferrer">PayPal</a> directly.
            </p>

            <h2>7. Data Retention</h2>
            <p>
              We retain your account data for as long as your account is active. If you request account deletion, we will remove your personal
              data from our systems. Some anonymized or aggregated data may be retained for analytical purposes. Crisp retains chat conversation
              data according to its own retention policy.
            </p>

            <h2>8. Your Rights</h2>
            <p>
              Depending on your location, you may have the following rights regarding your personal data:
            </p>
            <ul>
              <li><strong>Access</strong>: Request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification</strong>: Request correction of inaccurate or incomplete data.</li>
              <li><strong>Erasure</strong>: Request deletion of your account and associated personal data.</li>
              <li><strong>Portability</strong>: Request your data in a structured, machine-readable format.</li>
              <li><strong>Restriction</strong>: Request that we limit how we process your data in certain circumstances.</li>
              <li><strong>Objection</strong>: Object to our processing of your data where we rely on legitimate interests.</li>
              <li>
                <strong>Withdraw consent</strong>: Where processing is based on your consent (e.g., weekly email reports), you can withdraw
                consent at any time via your account settings.
              </li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:contact@kikikata.app">contact@kikikata.app</a>. If you believe we have not handled your data correctly, you have
              the right to lodge a complaint with your local data protection authority.
            </p>

            <h2>9. Cookies and Local Storage</h2>
            <p>
              We and our third-party partners use cookies and browser local storage. For full details, please see our{' '}
              <a href="/cookie-policy">Cookie Policy</a>.
            </p>

            <h2>10. Children&apos;s Privacy</h2>
            <p>
              Kikikata is not directed at children under 13. We do not knowingly collect personal data from children under 13. If you believe
              a child under 13 has provided us with personal data, please contact us and we will delete it promptly.
            </p>

            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically. Material changes will be communicated by updating the date at the top of this page.
              We encourage you to review this policy from time to time.
            </p>

            <h2>12. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or how we handle your data, please contact us at{' '}
              <a href="mailto:contact@kikikata.app">contact@kikikata.app</a>.
            </p>
          </article>
        </div>
      </main>

      <MainFooter />
    </div>
  );
};

export default PrivacyPolicyPage;